<template>
  <div class="statistics-overview">
    <div class="statistics-overview__cards">
      <div class="p-fieldset p-p-0 p-mb-0 statistics-overview__card statistics-overview__card--sales">
        <div class="p-p-3 statistics-overview__card-content">
          <span class="statistics-overview__label">{{ $t('overviewCustomerInvoices') }}</span>
          <strong class="statistics-overview__value">{{ formatCurrency(invoiceTotal) }}</strong>
          <span class="statistics-overview__detail">{{ $t('Count') }}: {{ invoiceCount }}</span>
        </div>
      </div>

      <div class="p-fieldset p-p-0 p-mb-0 statistics-overview__card statistics-overview__card--orders">
        <div class="p-p-3 statistics-overview__card-content">
          <span class="statistics-overview__label">{{ $t('overviewOrdersAndOffers') }}</span>
          <span class="statistics-overview__item">{{ $t('overviewOrdersCreated') }}: <strong>{{ createdOrdersCount }}</strong></span>
          <span class="statistics-overview__item">{{ $t('overviewOffersCreated') }}: <strong>{{ createdOffersCount }}</strong></span>
        </div>
      </div>

      <div class="p-fieldset p-p-0 p-mb-0 statistics-overview__card statistics-overview__card--customers" v-if="!$store.getters.isForkliftTenant">
        <div class="p-p-3 statistics-overview__card-content">
          <span class="statistics-overview__label">{{ $t('overviewCustomersAndVehicles') }}</span>
          <span class="statistics-overview__item">{{ $t('overviewNewCustomers') }}: <strong>{{ newCustomersCount }}</strong></span>
          <span class="statistics-overview__item">{{ $t('overviewNewVehicles') }}: <strong>{{ newVehiclesCount }}</strong></span>
        </div>
      </div>

      <div v-if="consumablesStat" class="p-fieldset p-p-0 p-mb-0 statistics-overview__card statistics-overview__card--consumables">
        <div class="p-p-3 statistics-overview__card-content">
          <span class="statistics-overview__label">{{ $t('consumablesStatisticsLabel') }}</span>
          <span class="statistics-overview__detail">{{ $t('purchasedLabel') }}: {{ formatCurrency(consumablesStat.purchased) }}</span>
          <span class="statistics-overview__detail">{{ $t('soldLabel') }}: {{ formatCurrency(consumablesStat.sold) }}</span>
          <span class="statistics-overview__detail">{{ $t('Profit') }}: {{ formatCurrency(consumablesProfit) }}</span>
        </div>
      </div>
    </div>

    <Panel :toggleable="false" class="p-mt-5">
      <template #header>
        <div class="statistics-panel-header">
          <b>{{ $t('overviewTrendTitle') }}</b>
          <span v-if="chartRangeLabel" class="p-text-secondary statistics-panel-date">
            {{ chartRangeLabel }}
          </span>
        </div>
      </template>
      <div>
        <div v-if="monthlyBuckets.length" class="statistics-overview__scroll">
          <div class="statistics-overview__chart">
            <div v-for="bucket in monthlyBuckets" :key="bucket.month" class="statistics-overview__column">
              <div class="statistics-overview__plot">
                <div class="statistics-overview__bar-slot" :style="{ height: barHeight(bucket.total) }">
                  <span class="statistics-overview__bar-value">{{ formatCurrency(bucket.total) }}</span>
                  <div class="statistics-overview__bar"
                       :class="{ 'statistics-overview__bar--empty': !bucket.total }"
                       role="img"
                       :aria-label="bucket.label + ': ' + $t('overviewCustomerInvoices') + ' ' + formatCurrency(bucket.total)"/>
                </div>
              </div>
              <div class="statistics-overview__tick">{{ bucket.label }}</div>
            </div>
          </div>
        </div>
        <div v-else class="statistics-overview__empty">{{ $t('No data available') }}</div>
      </div>
    </Panel>
  </div>
</template>

<script>
import formatMixins from '@/mixins/formatMixins'

export default {
  name: 'StatisticsOverview',
  mixins: [formatMixins],
  props: {
    invoices: { type: Object, default: null },
    consumablesStat: { type: Object, default: null },
    createdOrdersCount: { type: Number, default: 0 },
    createdOffersCount: { type: Number, default: 0 },
    newCustomersCount: { type: Number, default: 0 },
    newVehiclesCount: { type: Number, default: 0 },
    salesInvoiceTrend: { type: Array, default: () => [] },
    statisticsTimezone: { type: String, default: '' },
    toTimestamp: { default: null }
  },
  computed: {
    invoiceTotal() {
      return Number(this.invoices?.sales?.total || 0) + Number(this.invoices?.prepayment?.total || 0)
    },
    invoiceCount() {
      return Number(this.invoices?.sales?.count || 0) + Number(this.invoices?.prepayment?.count || 0)
    },
    consumablesProfit() {
      return Number(this.consumablesStat?.sold || 0) - Number(this.consumablesStat?.purchased || 0)
    },
    monthlyBuckets() {
      const formatter = new Intl.DateTimeFormat(this.$i18n.locale || 'en', {
        month: 'short', year: 'numeric'
      })
      return [...this.salesInvoiceTrend]
          .sort((a, b) => a.month.localeCompare(b.month))
          .map(row => {
            const [year, month] = row.month.split('-').map(Number)
            return {
              month: row.month,
              label: formatter.format(new Date(year, month - 1, 1)),
              total: Number(row.total || 0)
            }
          })
    },
    chartMax() {
      return Math.max(...this.monthlyBuckets.map(bucket => bucket.total), 0)
    },
    chartRangeLabel() {
      const first = this.monthlyBuckets[0]
      const last = this.monthlyBuckets[this.monthlyBuckets.length - 1]
      if (!first || !last) return ''
      const locale = this.$i18n.locale || 'en'
      const [firstYear, firstMonth] = first.month.split('-').map(Number)
      const [lastYear, lastMonth] = last.month.split('-').map(Number)
      const startDate = new Date(Date.UTC(firstYear, firstMonth - 1, 1))
      const startLabel = new Intl.DateTimeFormat(locale, {
        timeZone: 'UTC', day: 'numeric', month: 'long',
        ...(firstYear !== lastYear ? { year: 'numeric' } : {})
      }).format(startDate)
      const raw = Number(this.toTimestamp)
      const selectedEnd = this.toTimestamp == null ? null
          : Number.isFinite(raw) ? new Date(raw < 1e11 ? raw * 1000 : raw) : new Date(this.toTimestamp)
      const end = selectedEnd && !Number.isNaN(selectedEnd.getTime())
          ? selectedEnd : new Date(Date.UTC(lastYear, lastMonth, 0))
      const timeZone = end === selectedEnd
          ? this.statisticsTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone
          : 'UTC'
      const endLabel = new Intl.DateTimeFormat(locale, {
        timeZone, day: 'numeric', month: 'long', year: 'numeric'
      }).format(end)
      return `${startLabel} — ${endLabel}`
    }
  },
  methods: {
    barHeight(value) {
      if (!this.chartMax || value <= 0) return '4px'
      return `${Math.max(4, value / this.chartMax * 100)}%`
    }
  }
}
</script>

<style lang="scss" scoped>
.statistics-overview {
  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
  }
  &__card {
    min-width: 0;
    //box-shadow: 0 3px 12px rgba(27, 43, 65, .08);
    transition: box-shadow .18s ease, transform .18s ease;
    &:hover {
      box-shadow: 0 6px 18px rgba(27, 43, 65, .12);
      transform: translateY(-2px);
    }
  }
  &__card--sales { border-left: 4px solid #3b82f6; }
  &__card--orders { border-left: 4px solid #f5a524; }
  &__card--customers { border-left: 4px solid #27a69a; }
  &__card--consumables { border-left: 4px solid #8b5cf6; }
  &__card-content { display: flex; min-width: 0; flex-direction: column; gap: 6px; }
  &__label { color: var(--text-color-secondary, #59636e); font-weight: 600; }
  &__value { color: var(--text-color, #25313d); font-size: clamp(1.35rem, 2vw, 1.5rem); line-height: 1.15; overflow-wrap: anywhere; }
  &__item { color: var(--text-color, #25313d); strong { font-size: 1.05rem; } }
  &__detail { color: var(--text-color-secondary, #59636e); font-size: .92rem; line-height: 1; }
  &__scroll { overflow-x: auto; }
  &__chart { display: flex; min-width: 600px; height: 190px; gap: 12px; padding-top: 20px; }
  &__column { display: flex; flex: 1; min-width: 0; flex-direction: column; }
  &__plot {
    display: flex;
    flex: 1;
    align-items: flex-end;
    justify-content: center;
    padding-top: 24px;
    border-bottom: 1px solid var(--border-color);
  }
  &__bar-slot { position: relative; width: min(65%, 72px); }
  &__bar-value {
    position: absolute; bottom: calc(100% + 4px); left: 50%; transform: translateX(-50%);
    color: var(--text-color-secondary, #59636e); font-size: .84rem; white-space: nowrap;
  }
  &__bar {
    width: 100%; height: 100%;
    padding: 0;
    border: 0;
    border-radius: 4px 4px 0 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, .12) 100%), var(--primary-color);
    box-shadow: inset 1px 0 rgba(255, 255, 255, .16), 0 2px 6px rgba(27, 43, 65, .14);
    transition: filter .18s ease, box-shadow .18s ease;
    &:hover { filter: brightness(.94); box-shadow: inset 1px 0 rgba(255, 255, 255, .16), 0 3px 12px rgba(27, 43, 65, .24); }
    &--empty {
      background: var(--surface-border, #dfe3e8);
      box-shadow: none;
      &:hover { filter: none; box-shadow: none; }
    }
  }
  &__tick {
    min-height: 32px;
    padding-top: 8px;
    color: var(--text-color-secondary, #59636e);
    font-size: .75rem;
    text-align: center;
    white-space: nowrap;
  }
  &__empty { padding: 34px 0 18px; color: var(--text-color-secondary, #59636e); text-align: center; }
}

@media (max-width: 767px) {
  .statistics-overview__cards { grid-template-columns: 1fr; gap: 10px; }
}
@media (prefers-reduced-motion: reduce) {
  .statistics-overview__card { transition: none; &:hover { transform: none; } }
  .statistics-overview__bar { transition: none; }
}
</style>
